import React from 'react';
import { useAuth, SupportedLanguage } from '../../contexts/AuthContext';

const LANG_DISPLAY: Record<string, string> = {
  hindi: 'हिन्दी',
  gujarati: 'ગુજરાતી',
  marathi: 'मराठी',
};

interface LanguageToggleProps {
  // null/undefined = use user's preferred language (default).
  // "english"     = force English on this surface only.
  overrideLang: SupportedLanguage | null;
  onToggle: (newOverride: SupportedLanguage | null) => void;
  isDarkMode?: boolean;
  size?: 'sm' | 'xs';
  className?: string;
}

/**
 * Small per-surface language pill: lets a user with a non-English preference
 * temporarily peek at the English source of a TLDR / Pulse / Playbook without
 * changing their account-wide setting.
 *
 * Renders NOTHING for English-pref users (their account-wide setting already
 * matches what they'd see; no toggle needed).
 */
export const LanguageToggle: React.FC<LanguageToggleProps> = ({
  overrideLang,
  onToggle,
  isDarkMode = false,
  size = 'sm',
  className = '',
}) => {
  const { user } = useAuth();

  if (!user?.language || user.language === 'english') return null;

  const userLang = user.language;
  const isEnglish = overrideLang === 'english';

  const padding = size === 'xs' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs';
  const activeCls = isDarkMode ? 'bg-cyan-500 text-white' : 'bg-cyan-500 text-white';
  const inactiveCls = isDarkMode
    ? 'text-slate-400 hover:text-white'
    : 'text-slate-500 hover:text-slate-800';

  return (
    <div
      role="group"
      aria-label="Display language"
      className={`inline-flex rounded-md overflow-hidden ${
        isDarkMode ? 'bg-slate-800/60' : 'bg-slate-100'
      } ${className}`}
    >
      <button
        type="button"
        onClick={() => onToggle('english')}
        aria-pressed={isEnglish}
        className={`${padding} font-medium transition-colors ${
          isEnglish ? activeCls : inactiveCls
        }`}
      >
        EN
      </button>
      <button
        type="button"
        onClick={() => onToggle(null)}
        aria-pressed={!isEnglish}
        className={`${padding} font-medium transition-colors ${
          !isEnglish ? activeCls : inactiveCls
        }`}
      >
        {LANG_DISPLAY[userLang] ?? userLang}
      </button>
    </div>
  );
};
